import { isApiEnvelope, isValidationErrorBody } from '@/types/api';
import type { AccountChoice } from '@/types/auth';

/**
 * Every failure in the app is normalised into one `AppError` here, so no
 * component, hook, or boundary ever has to know about axios or about the
 * backend's two response shapes.
 *
 * This module is deliberately pure — it imports no axios instance and performs
 * no I/O — which makes the discrimination logic trivially unit-testable.
 */

export type ErrorKind =
  | 'network'
  | 'validation'
  | 'unauthorized'
  | 'forbidden'
  | 'notFound'
  | 'conflict'
  | 'rateLimited'
  | 'server'
  | 'unknown';

/** Field-keyed messages, matching Laravel's `errors` object. */
export type FieldErrors = Record<string, string[]>;

export class AppError extends Error {
  readonly status: number;
  readonly kind: ErrorKind;
  readonly fieldErrors: FieldErrors | undefined;
  /** The envelope's `data`, retained so callers can read structured payloads. */
  readonly data: unknown;

  constructor(init: {
    message: string;
    status: number;
    kind: ErrorKind;
    fieldErrors?: FieldErrors | undefined;
    data?: unknown;
    cause?: unknown;
  }) {
    super(init.message, init.cause !== undefined ? { cause: init.cause } : undefined);
    this.name = 'AppError';
    this.status = init.status;
    this.kind = init.kind;
    this.fieldErrors = init.fieldErrors;
    this.data = init.data;
  }
}

export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError;
}

/** Maps an HTTP status onto the coarse category the UI branches on. */
function kindForStatus(status: number): ErrorKind {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status === 409) return 'conflict';
  if (status === 422) return 'validation';
  if (status === 429) return 'rateLimited';
  if (status >= 500) return 'server';
  if (status === 0) return 'network';
  return 'unknown';
}

/** Human-readable fallbacks for responses with no usable body. */
function fallbackMessage(status: number): string {
  switch (kindForStatus(status)) {
    case 'network':
      return "Can't reach the server. Check your connection and try again.";
    case 'unauthorized':
      return 'Your session has ended. Please sign in again.';
    case 'forbidden':
      return "You don't have permission to do that.";
    case 'notFound':
      return "We couldn't find what you were looking for.";
    case 'rateLimited':
      return 'Too many attempts. Please wait a moment and try again.';
    case 'server':
      return 'Something went wrong on our end. Please try again shortly.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

interface ResponseLike {
  status: number;
  data: unknown;
}

interface AxiosLikeError {
  isAxiosError: true;
  message: string;
  response?: ResponseLike;
  code?: string;
}

function isAxiosLikeError(error: unknown): error is AxiosLikeError {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { isAxiosError?: unknown }).isAxiosError === true
  );
}

/**
 * Normalise anything thrown by the API layer into an `AppError`.
 *
 * Discrimination is by BODY STRUCTURE FIRST, status second. That ordering is
 * load-bearing: the backend returns a 422 *envelope* (no `errors` key) for an
 * expired password-reset token, which must surface as a form-level message —
 * branching on the 422 status alone would invent phantom field errors.
 */
export function toAppError(error: unknown): AppError {
  if (isAppError(error)) return error;

  if (!isAxiosLikeError(error)) {
    const message = error instanceof Error ? error.message : 'An unexpected error occurred.';
    return new AppError({ message, status: 0, kind: 'unknown', cause: error });
  }

  // No response at all: offline, DNS failure, timeout, or a CORS rejection
  // (which the browser reports to JS as an ordinary network error).
  if (!error.response) {
    return new AppError({
      message: fallbackMessage(0),
      status: 0,
      kind: 'network',
      cause: error,
    });
  }

  const { status, data: body } = error.response;

  // 1. Laravel validation body: { message, errors: { field: [...] } }
  if (isValidationErrorBody(body)) {
    return new AppError({
      message: body.message || 'Please check the highlighted fields.',
      status,
      kind: 'validation',
      fieldErrors: body.errors,
      cause: error,
    });
  }

  // 2. sendAjaxResponse envelope: { code, status, data, message }
  if (isApiEnvelope(body)) {
    return new AppError({
      message: body.message || fallbackMessage(status),
      status,
      kind: kindForStatus(status),
      data: body.data,
      cause: error,
    });
  }

  // 3. A bare framework exception body: { message }. Should be unreachable now
  //    that bootstrap/app.php envelopes the common ones, but cheap to keep.
  if (
    typeof body === 'object' &&
    body !== null &&
    typeof (body as { message?: unknown }).message === 'string'
  ) {
    return new AppError({
      message: (body as { message: string }).message,
      status,
      kind: kindForStatus(status),
      cause: error,
    });
  }

  // 4. Anything else: an HTML debug page, a proxy error, an empty body.
  return new AppError({
    message: fallbackMessage(status),
    status,
    kind: kindForStatus(status),
    cause: error,
  });
}

/**
 * Reads the account list off an `account_type_required` sign-in response.
 * Returns null for every other error, so callers can branch with one call.
 */
export function accountChoicesFrom(error: unknown): AccountChoice[] | null {
  if (!isAppError(error) || error.status !== 409) return null;

  const data = error.data as { reason?: string; accounts?: AccountChoice[] } | null | undefined;

  if (data?.reason !== 'account_type_required' || !Array.isArray(data.accounts)) return null;

  return data.accounts.length > 0 ? data.accounts : null;
}

/**
 * Whether a rejected code means the whole challenge is gone — expired, spent by
 * too many wrong guesses, or superseded by a fresh sign-in.
 *
 * Both cases are 401, and the difference decides where the user goes: without
 * this marker the code was merely wrong and they stay on the code step, with it
 * they have to sign in again. Neither is ever a reason to clear a stored token.
 */
export function isOtpChallengeExpired(error: unknown): boolean {
  if (!isAppError(error) || error.status !== 401) return false;

  const data = error.data as { reason?: string } | null | undefined;

  return data?.reason === 'otp_challenge_expired';
}

/** First message for a field, if the server reported one. */
export function firstFieldError(error: unknown, field: string): string | undefined {
  if (!isAppError(error)) return undefined;
  return error.fieldErrors?.[field]?.[0];
}
